import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { User, AlertTriangle, Clock, Calendar, Receipt, ChevronRight } from 'lucide-react';
import { EstadoPagoBadge, formatCurrency, formatDate } from './utils';

/**
 * Calcula días respecto a hoy (positivo = vencida, negativo = por vencer).
 * Devuelve null si la fecha es inválida.
 */
const daysFromToday = (isoDate) => {
  if (!isoDate) return null;
  try {
    const d = new Date(String(isoDate).slice(0, 10));
    if (isNaN(d.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return Math.round((today - d) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

/** Chip de vencimiento (vencida / por vencer / hoy). */
const VencimientoChip = ({ fechaVenc }) => {
  const diff = daysFromToday(fechaVenc);
  if (diff === null) return null;
  if (diff > 0) {
    return (
      <Badge variant="destructive" className="text-[10px] gap-1">
        <AlertTriangle className="w-3 h-3" />
        Vencida hace {diff} día{diff === 1 ? '' : 's'}
      </Badge>
    );
  }
  if (diff === 0) {
    return (
      <Badge variant="default" className="text-[10px] gap-1 bg-amber-500 hover:bg-amber-500/90">
        <Clock className="w-3 h-3" /> Vence hoy
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="text-[10px] gap-1">
      <Calendar className="w-3 h-3" />
      Vence en {Math.abs(diff)} día{Math.abs(diff) === 1 ? '' : 's'}
    </Badge>
  );
};

export const FacturasPendientesTab = ({
  facturasFiltradas,
  clientes,
  filtroCliente, setFiltroCliente,
  onPagar,
}) => {
  // Agrupar por cliente y calcular subtotal de saldo por cliente.
  const grupos = useMemo(() => {
    const map = new Map();
    facturasFiltradas.forEach((f) => {
      const key = f.cliente_id || f.cliente_nombre || '__sin_cliente__';
      const nombre = f.cliente_nombre || '— Sin cliente —';
      if (!map.has(key)) {
        map.set(key, { key, nombre, facturas: [], subtotalSaldo: 0, subtotalTotal: 0 });
      }
      const g = map.get(key);
      g.facturas.push(f);
      g.subtotalSaldo += Number(f.saldo || 0);
      g.subtotalTotal += Number(f.total_monto || 0);
    });
    // Orden: clientes con más saldo arriba; dentro de cada grupo, factura más
    // antigua primero (vencidas primero).
    const arr = Array.from(map.values());
    arr.forEach((g) => {
      g.facturas.sort((a, b) => String(a.fecha_vencimiento || '').localeCompare(String(b.fecha_vencimiento || '')));
    });
    arr.sort((a, b) => b.subtotalSaldo - a.subtotalSaldo);
    return arr;
  }, [facturasFiltradas]);

  const totalGeneral = useMemo(
    () => grupos.reduce((s, g) => s + g.subtotalSaldo, 0),
    [grupos],
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="space-y-1 min-w-0">
            <CardTitle className="text-base md:text-lg font-display font-600">
              Facturas Pendientes y Parciales
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Pendiente</span>: sin pagos ·{' '}
              <span className="font-medium text-foreground">Parcial</span>: con abonos pero saldo restante
            </p>
          </div>
          <Select value={filtroCliente} onValueChange={setFiltroCliente}>
            <SelectTrigger className="w-full sm:w-[260px]" data-testid="filtro-cliente">
              <SelectValue placeholder="Filtrar por cliente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los clientes</SelectItem>
              {clientes.map((cliente) => (
                <SelectItem key={cliente.id || cliente._id} value={cliente.id || cliente._id}>
                  {cliente.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent>
        {facturasFiltradas.length === 0 ? (
          <div className="text-center py-14 text-muted-foreground">
            <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No hay facturas pendientes</p>
          </div>
        ) : (
          <div className="space-y-5" data-testid="lista-facturas-pendientes">
            {grupos.map((g) => (
              <div
                key={g.key}
                className="rounded-lg border border-border bg-card overflow-hidden"
                data-testid={`grupo-cliente-${g.key}`}
              >
                {/* Cabecera de cliente (divisor visual) */}
                <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/10 border-b-2 border-primary/40">
                  <div className="flex items-center gap-2 min-w-0">
                    <User className="w-4 h-4 text-primary shrink-0" />
                    <h4 className="font-semibold text-sm truncate" style={{ fontFamily: 'Space Grotesk' }}>
                      {g.nombre}
                    </h4>
                    <Badge variant="outline" className="text-[10px] tabular-nums">
                      {g.facturas.length} factura{g.facturas.length === 1 ? '' : 's'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground">Saldo total:</span>
                    <span className="text-sm font-bold tabular-nums text-destructive">
                      {formatCurrency(g.subtotalSaldo)}
                    </span>
                  </div>
                </div>

                {/* Filas de facturas del cliente */}
                <ul className="divide-y divide-border">
                  {g.facturas.map((factura) => {
                    const diff = daysFromToday(factura.fecha_vencimiento);
                    const vencida = diff !== null && diff > 0;
                    return (
                      <li
                        key={factura.id || factura._id}
                        className={`flex items-center gap-3 px-4 py-3 hover:bg-accent/40 transition-colors ${vencida ? 'border-l-4 border-l-destructive' : 'border-l-4 border-l-transparent'}`}
                        data-testid={`factura-${factura.numero_factura}`}
                      >
                        {/* Columna izquierda: N° + estado + vencimiento */}
                        <div className="flex flex-col gap-1 min-w-[140px]">
                          <span className="font-semibold text-sm tabular-nums">
                            {factura.numero_factura}
                          </span>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <EstadoPagoBadge estado={factura.estado_pago} />
                          </div>
                        </div>

                        {/* Columna central: fechas */}
                        <div className="flex-1 min-w-0 hidden sm:block">
                          <div className="flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
                            <span>
                              <span className="text-foreground/60">Emisión</span>{' '}
                              <span className="font-medium text-foreground">{formatDate(factura.fecha)}</span>
                            </span>
                            <ChevronRight className="w-3 h-3 opacity-50" />
                            <span>
                              <span className="text-foreground/60">Vence</span>{' '}
                              <span className={`font-medium ${vencida ? 'text-destructive' : 'text-foreground'}`}>
                                {formatDate(factura.fecha_vencimiento)}
                              </span>
                            </span>
                          </div>
                          <div className="mt-1">
                            <VencimientoChip fechaVenc={factura.fecha_vencimiento} />
                          </div>
                        </div>

                        {/* Columna montos */}
                        <div className="flex flex-col items-end gap-0.5 min-w-[130px]">
                          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Total</span>
                          <span className="text-sm tabular-nums text-muted-foreground">
                            {formatCurrency(factura.total_monto)}
                          </span>
                          {Number(factura.total_monto || 0) > Number(factura.saldo || 0) + 0.01 && (
                            <>
                              <span className="text-[10px] text-muted-foreground uppercase tracking-wide mt-1">Abonado</span>
                              <span className="text-xs tabular-nums text-emerald-700 dark:text-emerald-400 font-medium">
                                {formatCurrency(Number(factura.total_monto || 0) - Number(factura.saldo || 0))}
                              </span>
                            </>
                          )}
                          <span className="text-[10px] text-muted-foreground uppercase tracking-wide mt-1">Saldo</span>
                          <span className="text-base font-bold tabular-nums text-destructive">
                            {formatCurrency(factura.saldo)}
                          </span>
                        </div>

                        {/* Acción */}
                        <Button
                          size="sm"
                          onClick={() => onPagar(factura)}
                          data-testid={`btn-pagar-${factura.numero_factura}`}
                          className="shrink-0"
                        >
                          {factura.estado_pago === 'Parcial' ? 'Abonar' : 'Registrar Pago'}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            {/* Total general */}
            <div className="flex items-center justify-end gap-3 px-4 py-3 rounded-lg bg-destructive/10 border border-destructive/30">
              <span className="text-sm font-medium text-destructive/80">
                Total general pendiente ({grupos.length} cliente{grupos.length === 1 ? '' : 's'})
              </span>
              <span className="text-lg font-bold tabular-nums text-destructive">
                {formatCurrency(totalGeneral)}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
